import React from 'react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { CheckIcon, XIcon } from 'lucide-react';
export default function Pricing() {
  const plans = [{
    name: 'Silver',
    price: '£99',
    period: 'per year',
    description: 'For individuals who want reliable daily reminders.',
    features: ['Daily SMS reminders', 'Standard call reminders', 'Track medication and appointments', 'Dashboard for 1 family member', 'Email support'],
    stripeLink: 'https://buy.stripe.com/fZu5kEa7H65t4dAbcw2B200',
    highlighted: false
  }, {
    name: 'Gold',
    price: '£120',
    period: 'per year',
    description: 'For users with frequent medical routines and family involvement.',
    features: ['Everything in Silver, plus:', 'Unlimited reminders', 'Dashboard for up to 3 family members', 'Priority delivery queue', 'Carer note sharing'],
    stripeLink: 'https://buy.stripe.com/7sY4gA0x7fG325sa8s2B201',
    highlighted: true
  }, {
    name: 'Platinum',
    price: '£150',
    period: 'per year',
    description: 'For families requiring maximum visibility and support.',
    features: ['Everything in Gold, plus:', 'Unlimited family members', 'Instant notifications', 'Monthly wellbeing summary', 'Scheduled check-in calls', 'Priority support'],
    stripeLink: 'https://buy.stripe.com/3cI5kE4Nn9hF25s80k2B202',
    highlighted: false
  }];
  const comparisonFeatures = [{
    feature: 'Daily SMS reminders',
    silver: true,
    gold: true,
    platinum: true
  }, {
    feature: 'Standard call reminders',
    silver: true,
    gold: true,
    platinum: true
  }, {
    feature: 'Track medication and appointments',
    silver: true,
    gold: true,
    platinum: true
  }, {
    feature: 'Email support',
    silver: true,
    gold: true,
    platinum: true
  }, {
    feature: 'Family dashboard members',
    silver: '1',
    gold: '3',
    platinum: 'Unlimited'
  }, {
    feature: 'Unlimited reminders',
    silver: false,
    gold: true,
    platinum: true
  }, {
    feature: 'Priority delivery queue',
    silver: false,
    gold: true,
    platinum: true
  }, {
    feature: 'Carer note sharing',
    silver: false,
    gold: true,
    platinum: true
  }, {
    feature: 'Instant notifications',
    silver: false,
    gold: false,
    platinum: true
  }, {
    feature: 'Monthly wellbeing summary',
    silver: false,
    gold: false,
    platinum: true
  }, {
    feature: 'Scheduled check-in calls',
    silver: false,
    gold: false,
    platinum: true
  }, {
    feature: 'Priority support',
    silver: false,
    gold: false,
    platinum: true
  }];
  return <div className="w-full" data-id="element-418">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-light-grey py-16" data-id="element-419">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-420">
          <h1 className="text-4xl md:text-5xl font-bold text-navy font-heading mb-4" data-id="element-421">
            Simple Plans for Peace of Mind
          </h1>
          <p className="text-xl text-dark" data-id="element-422">
            Choose the plan that best suits your family.
          </p>
        </div>
      </section>
      {/* Pricing Cards */}
      <section className="py-16 bg-white" data-id="element-423">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-424">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-425">
            {plans.map((plan, index) => <Card key={index} className={`flex flex-col h-full ${plan.highlighted ? 'ring-2 ring-sky-blue shadow-xl' : ''}`} data-id="element-426">
                {plan.highlighted && <div className="bg-sky-blue text-white text-center py-2 -mt-5 -mx-5 mb-5 rounded-t-lg font-semibold" data-id="element-427">
                    Most Popular
                  </div>}
                <div className="flex-grow" data-id="element-428">
                  <h3 className="text-2xl font-bold text-navy mb-2" data-id="element-429">
                    {plan.name}
                  </h3>
                  <div className="mb-4" data-id="element-430">
                    <span className="text-4xl font-bold text-navy" data-id="element-431">
                      {plan.price}
                    </span>
                    <span className="text-dark ml-2" data-id="element-432">{plan.period}</span>
                  </div>
                  <p className="text-dark mb-6" data-id="element-433">{plan.description}</p>
                  <ul className="space-y-3 mb-8" data-id="element-434">
                    {plan.features.map((feature, featureIndex) => <li key={featureIndex} className="flex items-start" data-id="element-435">
                        <CheckIcon className="h-5 w-5 text-sky-blue mr-2 flex-shrink-0 mt-0.5" data-id="element-436" />
                        <span className="text-dark" data-id="element-437">{feature}</span>
                      </li>)}
                  </ul>
                </div>
                <a href={plan.stripeLink} className={`block text-center px-6 py-3 rounded-lg font-semibold transition-colors ${plan.highlighted ? 'bg-sky-blue text-white hover:bg-sky-blue/90' : 'bg-navy text-white hover:bg-navy/90'}`} target="_blank" rel="noopener noreferrer" data-id="element-438">
                  Pay Securely
                </a>
              </Card>)}
          </div>
          <div className="mt-12 text-center" data-id="element-439">
            <p className="text-sm text-dark/60" data-id="element-440">
              All plans billed annually. 14-day cancellation period applies.
              Secure payments handled by Stripe.
            </p>
          </div>
        </div>
      </section>
      {/* Feature Comparison Table */}
      <section className="py-16 bg-light-grey" data-id="element-441">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-442">
          <div className="text-center mb-12" data-id="element-443">
            <h2 className="text-3xl font-bold text-navy font-heading mb-4" data-id="element-444">
              Compare Plans
            </h2>
            <p className="text-lg text-dark" data-id="element-445">
              See what is included in each plan at a glance.
            </p>
          </div>
          <Card className="overflow-x-auto" data-id="element-446">
            <table className="w-full" data-id="element-447">
              <thead data-id="element-448">
                <tr className="border-b border-light-grey" data-id="element-449">
                  <th className="text-left py-4 px-4 font-bold text-navy" data-id="element-450">
                    Feature
                  </th>
                  <th className="text-center py-4 px-4 font-bold text-navy" data-id="element-451">
                    Silver
                  </th>
                  <th className="text-center py-4 px-4 font-bold text-navy" data-id="element-452">
                    Gold
                  </th>
                  <th className="text-center py-4 px-4 font-bold text-navy" data-id="element-453">
                    Platinum
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-454">
                {comparisonFeatures.map((item, index) => <tr key={index} className={index % 2 === 0 ? 'bg-light-grey/30' : ''} data-id="element-455">
                    <td className="py-4 px-4 text-dark" data-id="element-456">{item.feature}</td>
                    <td className="py-4 px-4 text-center" data-id="element-457">
                      {typeof item.silver === 'boolean' ? item.silver ? <CheckIcon className="h-5 w-5 text-sky-blue mx-auto" data-id="element-458" /> : <XIcon className="h-5 w-5 text-dark/30 mx-auto" data-id="element-459" /> : <span className="text-dark font-medium" data-id="element-460">
                          {item.silver}
                        </span>}
                    </td>
                    <td className="py-4 px-4 text-center" data-id="element-461">
                      {typeof item.gold === 'boolean' ? item.gold ? <CheckIcon className="h-5 w-5 text-sky-blue mx-auto" data-id="element-462" /> : <XIcon className="h-5 w-5 text-dark/30 mx-auto" data-id="element-463" /> : <span className="text-dark font-medium" data-id="element-464">
                          {item.gold}
                        </span>}
                    </td>
                    <td className="py-4 px-4 text-center" data-id="element-465">
                      {typeof item.platinum === 'boolean' ? item.platinum ? <CheckIcon className="h-5 w-5 text-sky-blue mx-auto" data-id="element-466" /> : <XIcon className="h-5 w-5 text-dark/30 mx-auto" data-id="element-467" /> : <span className="text-dark font-medium" data-id="element-468">
                          {item.platinum}
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </Card>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 bg-white" data-id="element-469">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-470">
          <div className="text-center mb-12" data-id="element-471">
            <h2 className="text-3xl font-bold text-navy font-heading mb-4" data-id="element-472">
              Questions About Pricing?
            </h2>
            <p className="text-lg text-dark" data-id="element-473">
              We are here to help you choose the right plan.
            </p>
          </div>
          <div className="text-center" data-id="element-474">
            <Button size="lg" href="/faq" data-id="element-475">
              View FAQs
            </Button>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="bg-navy text-white py-16" data-id="element-476">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-477">
          <h2 className="text-3xl font-bold font-heading mb-4" data-id="element-478">
            Not Sure Which Plan to Choose?
          </h2>
          <p className="text-lg text-white/80 max-w-3xl mx-auto mb-8" data-id="element-479">
            Contact our team and we will help you find the perfect plan for your
            family's needs.
          </p>
          <Button variant="secondary" size="lg" href="/contact" data-id="element-480">
            Get in Touch
          </Button>
        </div>
      </section>
    </div>;
}